import { Component, OnDestroy, OnInit, inject, ChangeDetectionStrategy, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subject, timer } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { VideoStatusCardComponent } from '../../features/video/view/video-status-card/video-status-card.component';
import {
  VideoStatusService,
  VideoStatusProcessFilter,
  VideoStatusSortOrder
} from '../../features/video/controller/video-status.service';
import { RelaunchPipelineResponse, VideoLabService } from '../../features/video/controller/video-lab.service';
import { ConfirmationModalComponent, ConfirmationModalData } from '../../fragments/confirmation-modal/confirmation-modal.component';

@Component({
  selector: 'app-video-status-page',
  standalone: true,
  imports: [
    CommonModule,
    MatPaginatorModule,
    VideoStatusCardComponent
  ],
  templateUrl: './video-status-page.component.html',
  styleUrls: ['./video-status-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class VideoStatusPageComponent implements OnInit, OnDestroy {
  public statusService = inject(VideoStatusService);
  private labService = inject(VideoLabService);
  private dialog = inject(MatDialog);
  private snackBar = inject(MatSnackBar);
  public canRetry = signal<boolean>(false);
  private destroy$ = new Subject<void>();

  public isRetrying = signal<string | null>(null);
  public isArchiving = signal<string | null>(null);
  public isRetryingAll = signal<boolean>(false);
  public isPrioritizing = signal<string | null>(null);
  public isSearchOpen = signal<boolean>(false);
  public searchInputValue = signal<string>('');

  public readonly processFilters: Array<{ value: VideoStatusProcessFilter; label: string }> = [
    { value: 'all', label: 'Todos' },
    { value: 'processing', label: 'En procesamiento' },
    { value: 'queued', label: 'En cola' },
    { value: 'completed', label: 'Completos' },
    { value: 'error', label: 'Con error' }
  ];

  public readonly queueSortOptions: Array<{ value: VideoStatusSortOrder; label: string }> = [
    { value: 'queue_priority_desc', label: 'Prioridad alta → baja' },
    { value: 'queue_priority_asc', label: 'Prioridad baja → alta' }
  ];

  public readonly timelineSortOptions: Array<{ value: VideoStatusSortOrder; label: string }> = [
    { value: 'latest_desc', label: 'Último estado: más reciente' },
    { value: 'latest_asc', label: 'Último estado: más antiguo' }
  ];

  public readonly sortOptions = computed(() => {
    const isQueuedView = this.statusService.processFilter() === 'queued';
    return isQueuedView ? this.queueSortOptions : this.timelineSortOptions;
  });

  public readonly currentSortLabel = computed(() => {
    const selected = this.sortOptions().find((sort) => sort.value === this.statusService.sortOrder());
    return selected?.label ?? '';
  });

  public readonly processingCount = computed(() => this.statusService.statusOverview().processing);
  public readonly queuedCount = computed(() => this.statusService.statusOverview().queued);
  public readonly completedCount = computed(() => this.statusService.statusOverview().completed);
  public readonly errorCount = computed(() => this.statusService.statusOverview().error);

  public readonly activeFilterLabel = computed(() => {
    const selected = this.processFilters.find((filter) => filter.value === this.statusService.processFilter());
    return selected?.label ?? 'Todos';
  });

  public readonly hasCustomView = computed(() => {
    const currentFilter = this.statusService.processFilter();
    const defaultSort = currentFilter === 'queued' ? 'queue_priority_desc' : 'latest_desc';
    return currentFilter !== 'all' || this.statusService.sortOrder() !== defaultSort;
  });

  public readonly emptyStateMessage = computed(() => {
    const selectedFilter = this.statusService.processFilter();
    if (selectedFilter === 'processing') return 'No hay vídeos en procesamiento para este criterio.';
    if (selectedFilter === 'queued') return 'No hay vídeos en cola para este criterio.';
    if (selectedFilter === 'completed') return 'No hay vídeos completos para este criterio.';
    if (selectedFilter === 'error') return 'No hay vídeos en error (últimos 7 días) para este criterio.';
    return 'No hay vídeos en el panel de seguimiento en este momento.';
  });

  ngOnInit(): void {
    this.labService.checkInternalAccess().subscribe({
      next: (resp) => this.canRetry.set(resp),
      error: () => this.canRetry.set(false)
    });

    this.refreshData();

    timer(15000, 15000)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (!this.statusService.isLoading()) {
          this.statusService.loadComprehensiveStatuses();
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.statusService.clearState();
  }

  refreshData(): void {
    this.statusService.loadComprehensiveStatuses();
  }

  onPageEvent(e: PageEvent): void {
    const pageIndex = e.pageIndex + 1;

    if (e.pageSize !== this.statusService.pageSize()) {
      this.statusService.setPageSize(e.pageSize);
    } else {
      this.statusService.setPage(pageIndex);
    }
  }

  onProcessFilterChange(filter: VideoStatusProcessFilter): void {
    this.statusService.setProcessFilter(filter);
  }

  onSortOrderChange(sortOrder: VideoStatusSortOrder): void {
    this.statusService.setSortOrder(sortOrder);
  }

  toggleSearch(): void {
    const opening = !this.isSearchOpen();
    this.isSearchOpen.set(opening);
    if (!opening) {
      this.searchInputValue.set('');
      this.statusService.setSearchQuery('');
    }
  }

  onSearchInputChange(rawValue: string): void {
    this.searchInputValue.set(rawValue);
  }

  applySearch(): void {
    this.statusService.setSearchQuery(this.searchInputValue());
  }

  clearSearch(): void {
    this.searchInputValue.set('');
    this.statusService.setSearchQuery('');
  }

  onRetryVideo(videoId: string): void {
    this.executeRetryVideo(videoId, false);
  }

  onRetryAllVideos(): void {
    if (this.isRetryingAll()) return;

    const dialogRef = this.dialog.open<ConfirmationModalComponent, ConfirmationModalData>(ConfirmationModalComponent, {
      width: '560px',
      data: {
        title: 'Reprocesar todos los vídeos',
        message: 'Esta acción debe ser consultada con el equipo.<br><br>Pondrá en cola todos los vídeos de la web y puede tardar días en procesarse.<br><br>¿Confirmas que quieres continuar?',
        confirmButtonText: 'Sí, reprocesar todo'
      }
    });

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (confirmed !== true) return;

      this.isRetryingAll.set(true);
      this.labService.relaunchAllReadyPipelines().subscribe({
        next: () => {
          this.snackBar.open('Reprocesado masivo lanzado. Revisa el estado en los próximos minutos.', 'OK', {
            duration: 4500
          });
          this.refreshData();
          this.isRetryingAll.set(false);
        },
        error: (err) => {
          console.error('Error relaunching all videos:', err);
          this.snackBar.open('No se pudo iniciar el reprocesado masivo.', 'Cerrar', {
            duration: 4500
          });
          this.isRetryingAll.set(false);
        }
      });
    });
  }

  onArchiveVideo(videoId: string): void {
    const dialogRef = this.dialog.open<ConfirmationModalComponent, ConfirmationModalData>(ConfirmationModalComponent, {
      width: '560px',
      data: {
        title: 'Archivar vídeo',
        message: 'El video se archivará y no será accesible, además se eliminará su historial de procesamiento.<br><br>¿Estás seguro?',
        confirmButtonText: 'Sí, archivar'
      }
    });

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (confirmed !== true) return;

      this.isArchiving.set(videoId);
      this.labService.archiveVideo(videoId).subscribe({
        next: () => {
          this.snackBar.open('Vídeo archivado correctamente.', 'OK', {
            duration: 3500
          });
          this.refreshData();
          this.isArchiving.set(null);
        },
        error: (err) => {
          console.error('Error archiving video:', err);
          this.snackBar.open('No se pudo archivar el vídeo.', 'Cerrar', {
            duration: 4000
          });
          this.isArchiving.set(null);
        }
      });
    });
  }

  onPrioritizeVideo(videoId: string): void {
    if (this.isPrioritizing()) return;

    this.isPrioritizing.set(videoId);
    this.labService.prioritizeVideoInQueue(videoId).subscribe({
      next: () => {
        this.snackBar.open('Vídeo priorizado y movido al inicio de la cola prioritaria.', 'OK', {
          duration: 3500
        });
        this.refreshData();
        this.isPrioritizing.set(null);
      },
      error: (err) => {
        console.error('Error prioritizing video:', err);
        this.snackBar.open('No se pudo priorizar el vídeo en cola.', 'Cerrar', {
          duration: 4000
        });
        this.isPrioritizing.set(null);
      }
    });
  }

  private executeRetryVideo(videoId: string, forceRestart: boolean): void {
    this.isRetrying.set(videoId);

    this.labService.relaunchPipeline(videoId, forceRestart).subscribe({
      next: (res: RelaunchPipelineResponse) => {
        if (res.status === 'needs_confirmation' && !forceRestart) {
          this.isRetrying.set(null);
          const dialogRef = this.dialog.open<ConfirmationModalComponent, ConfirmationModalData>(ConfirmationModalComponent, {
            width: '540px',
            data: {
              title: 'Vídeo en procesamiento',
              message: 'El vídeo está en PROCESSING en el orquestador.<br><br>¿Deseas pararlo y relanzarlo?',
              confirmButtonText: 'Parar y relanzar'
            }
          });

          dialogRef.afterClosed().subscribe((confirmed) => {
            if (confirmed !== true) return;
            this.executeRetryVideo(videoId, true);
          });
          return;
        }

        if (res?.message) {
          this.snackBar.open(res.message, 'OK', { duration: 3500 });
        }

        // Pequeño delay para que el usuario vea que algo pasó
        setTimeout(() => {
          this.refreshData();
          this.isRetrying.set(null);
        }, 1000);
      },
      error: (err) => {
        console.error('Error relaunching:', err);
        this.snackBar.open('No se pudo relanzar el vídeo.', 'Cerrar', { duration: 4000 });
        this.isRetrying.set(null);
      }
    });
  }
  onCardClick(videoId: string): void {
    console.log('Navegar a detalle de:', videoId);
  }
}
